import { supabase } from '../lib/supabase';
import Papa from 'papaparse';

export const payrollIntegrationService = {
  // ============================================================================
  // PROVIDER CONFIGURATION
  // ============================================================================

  async getAllProviderConfigs() {
    const { data, error } = await supabase
      ?.from('payroll_provider_configs')
      ?.select('*')
      ?.order('provider_name', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      providerName: row?.provider_name,
      integrationMethod: row?.integration_method,
      integrationStatus: row?.integration_status,
      apiEndpoint: row?.api_endpoint,
      csvTemplateUrl: row?.csv_template_url,
      excelTemplateUrl: row?.excel_template_url,
      fieldMapping: row?.field_mapping,
      syncFrequency: row?.sync_frequency,
      lastSyncAt: row?.last_sync_at,
      nextSyncAt: row?.next_sync_at,
      isActive: row?.is_active,
      createdAt: row?.created_at
    })) || [];
  },

  async getProviderConfigById(configId) {
    const { data, error } = await supabase
      ?.from('payroll_provider_configs')
      ?.select('*')
      ?.eq('id', configId)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      providerName: data?.provider_name,
      integrationMethod: data?.integration_method,
      integrationStatus: data?.integration_status,
      apiEndpoint: data?.api_endpoint,
      apiVersion: data?.api_version,
      csvTemplateUrl: data?.csv_template_url,
      excelTemplateUrl: data?.excel_template_url,
      fieldMapping: data?.field_mapping,
      syncFrequency: data?.sync_frequency,
      lastSyncAt: data?.last_sync_at,
      isActive: data?.is_active,
      configurationMetadata: data?.configuration_metadata
    };
  },

  async createProviderConfig(config) {
    const { data, error } = await supabase
      ?.from('payroll_provider_configs')
      ?.insert({
        provider_name: config?.providerName,
        integration_method: config?.integrationMethod,
        integration_status: config?.integrationStatus || 'pending_setup',
        api_endpoint: config?.apiEndpoint,
        api_key_encrypted: config?.apiKeyEncrypted,
        api_version: config?.apiVersion,
        csv_template_url: config?.csvTemplateUrl,
        excel_template_url: config?.excelTemplateUrl,
        field_mapping: config?.fieldMapping,
        sync_frequency: config?.syncFrequency,
        is_active: config?.isActive !== undefined ? config?.isActive : true,
        configuration_metadata: config?.configurationMetadata,
        created_by: config?.createdBy
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      providerName: data?.provider_name,
      integrationMethod: data?.integration_method,
      integrationStatus: data?.integration_status
    };
  },

  async updateProviderConfig(configId, updates) {
    const updateData = {
      updated_at: new Date()?.toISOString()
    };

    if (updates?.integrationStatus) updateData.integration_status = updates?.integrationStatus;
    if (updates?.apiEndpoint) updateData.api_endpoint = updates?.apiEndpoint;
    if (updates?.apiKeyEncrypted) updateData.api_key_encrypted = updates?.apiKeyEncrypted;
    if (updates?.fieldMapping) updateData.field_mapping = updates?.fieldMapping;
    if (updates?.syncFrequency) updateData.sync_frequency = updates?.syncFrequency;
    if (updates?.lastSyncAt) updateData.last_sync_at = updates?.lastSyncAt;
    if (updates?.nextSyncAt) updateData.next_sync_at = updates?.nextSyncAt;
    if (updates?.isActive !== undefined) updateData.is_active = updates?.isActive;

    const { data, error } = await supabase
      ?.from('payroll_provider_configs')
      ?.update(updateData)
      ?.eq('id', configId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      integrationStatus: data?.integration_status,
      isActive: data?.is_active,
      updatedAt: data?.updated_at
    };
  },

  // ============================================================================
  // INTEGRATION SESSIONS
  // ============================================================================

  async createIntegrationSession(session) {
    const { data, error } = await supabase
      ?.from('payroll_integration_sessions')
      ?.insert({
        provider_config_id: session?.providerConfigId,
        session_name: session?.sessionName,
        pay_period_start: session?.payPeriodStart,
        pay_period_end: session?.payPeriodEnd,
        integration_method: session?.integrationMethod,
        session_status: 'pending',
        file_name: session?.fileName,
        file_size_bytes: session?.fileSizeBytes,
        initiated_by: session?.initiatedBy
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      sessionName: data?.session_name,
      sessionStatus: data?.session_status,
      initiatedAt: data?.initiated_at
    };
  },

  async getAllIntegrationSessions() {
    const { data, error } = await supabase
      ?.from('payroll_integration_sessions')
      ?.select(`
        *,
        payroll_provider_configs (provider_name, integration_method)
      `)
      ?.order('initiated_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      providerConfigId: row?.provider_config_id,
      providerName: row?.payroll_provider_configs?.provider_name,
      sessionName: row?.session_name,
      payPeriodStart: row?.pay_period_start,
      payPeriodEnd: row?.pay_period_end,
      integrationMethod: row?.integration_method,
      sessionStatus: row?.session_status,
      totalRecords: row?.total_records,
      processedRecords: row?.processed_records,
      failedRecords: row?.failed_records,
      initiatedBy: row?.initiated_by,
      initiatedAt: row?.initiated_at,
      completedAt: row?.completed_at
    })) || [];
  },

  async getIntegrationSessionById(sessionId) {
    const { data, error } = await supabase
      ?.from('payroll_integration_sessions')
      ?.select(`
        *,
        payroll_provider_configs (*)
      `)
      ?.eq('id', sessionId)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      providerConfig: {
        id: data?.payroll_provider_configs?.id,
        providerName: data?.payroll_provider_configs?.provider_name,
        integrationMethod: data?.payroll_provider_configs?.integration_method,
        fieldMapping: data?.payroll_provider_configs?.field_mapping
      },
      sessionName: data?.session_name,
      payPeriodStart: data?.pay_period_start,
      payPeriodEnd: data?.pay_period_end,
      sessionStatus: data?.session_status,
      totalRecords: data?.total_records,
      processedRecords: data?.processed_records,
      failedRecords: data?.failed_records,
      validationErrors: data?.validation_errors,
      initiatedAt: data?.initiated_at,
      completedAt: data?.completed_at
    };
  },

  async updateIntegrationSessionStatus(sessionId, status, updates = {}) {
    const updateData = {
      session_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (updates?.totalRecords !== undefined) updateData.total_records = updates?.totalRecords;
    if (updates?.processedRecords !== undefined) updateData.processed_records = updates?.processedRecords;
    if (updates?.failedRecords !== undefined) updateData.failed_records = updates?.failedRecords;
    if (updates?.validationErrors) updateData.validation_errors = updates?.validationErrors;
    if (status === 'completed') updateData.completed_at = new Date()?.toISOString();

    const { data, error } = await supabase
      ?.from('payroll_integration_sessions')
      ?.update(updateData)
      ?.eq('id', sessionId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      sessionStatus: data?.session_status,
      processedRecords: data?.processed_records,
      completedAt: data?.completed_at
    };
  },

  // ============================================================================
  // INTEGRATION RECORDS
  // ============================================================================

  async createIntegrationRecord(record) {
    const { data, error } = await supabase
      ?.from('payroll_integration_records')
      ?.insert({
        integration_session_id: record?.integrationSessionId,
        employee_id: record?.employeeId,
        employee_number: record?.employeeNumber,
        employee_name: record?.employeeName,
        department: record?.department,
        gross_pay: record?.grossPay,
        base_pay: record?.basePay,
        overtime_pay: record?.overtimePay,
        allowances: record?.allowances,
        deductions: record?.deductions,
        swt_amount: record?.swtAmount,
        nasfund_employee: record?.nasfundEmployee,
        nasfund_employer: record?.nasfundEmployer,
        net_pay: record?.netPay,
        hours_worked: record?.hoursWorked,
        overtime_hours: record?.overtimeHours,
        validation_status: record?.validationStatus || 'passed',
        validation_messages: record?.validationMessages,
        raw_data: record?.rawData
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      employeeNumber: data?.employee_number,
      validationStatus: data?.validation_status
    };
  },

  async getIntegrationRecords(sessionId) {
    const { data, error } = await supabase
      ?.from('payroll_integration_records')
      ?.select('*')
      ?.eq('integration_session_id', sessionId)
      ?.order('employee_number', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      employeeNumber: row?.employee_number,
      employeeName: row?.employee_name,
      department: row?.department,
      grossPay: row?.gross_pay,
      basePay: row?.base_pay,
      overtimePay: row?.overtime_pay,
      allowances: row?.allowances,
      deductions: row?.deductions,
      swtAmount: row?.swt_amount,
      nasfundEmployee: row?.nasfund_employee,
      nasfundEmployer: row?.nasfund_employer,
      netPay: row?.net_pay,
      hoursWorked: row?.hours_worked,
      overtimeHours: row?.overtime_hours,
      validationStatus: row?.validation_status,
      validationMessages: row?.validation_messages
    })) || [];
  },

  // ============================================================================
  // CSV/EXCEL IMPORT
  // ============================================================================

  async importFromCSV(file, providerConfigId, payPeriodStart, payPeriodEnd, initiatedBy) {
    return new Promise((resolve, reject) => {
      Papa?.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            // Create integration session
            const session = await this.createIntegrationSession({
              providerConfigId,
              sessionName: `CSV Import - ${payPeriodStart} to ${payPeriodEnd}`,
              payPeriodStart,
              payPeriodEnd,
              integrationMethod: 'csv_import',
              fileName: file?.name,
              fileSizeBytes: file?.size,
              initiatedBy
            });

            // Update session status to processing
            await this.updateIntegrationSessionStatus(session?.id, 'processing', {
              totalRecords: results?.data?.length
            });

            // Get provider config for field mapping
            const config = await this.getProviderConfigById(providerConfigId);
            const fieldMapping = config?.fieldMapping || {};

            // Process records
            let processedCount = 0;
            let failedCount = 0;
            const validationResults = [];

            for (const row of results?.data) {
              try {
                const record = {
                  integrationSessionId: session?.id,
                  employeeNumber: row?.[fieldMapping?.employee_number || 'Employee Number'],
                  employeeName: row?.[fieldMapping?.employee_name || 'Employee Name'],
                  department: row?.[fieldMapping?.department || 'Department'],
                  grossPay: parseFloat(row?.[fieldMapping?.gross_pay || 'Gross Pay'] || 0),
                  basePay: parseFloat(row?.[fieldMapping?.base_pay || 'Base Pay'] || 0),
                  overtimePay: parseFloat(row?.[fieldMapping?.overtime_pay || 'Overtime Pay'] || 0),
                  allowances: parseFloat(row?.[fieldMapping?.allowances || 'Allowances'] || 0),
                  deductions: parseFloat(row?.[fieldMapping?.deductions || 'Deductions'] || 0),
                  swtAmount: parseFloat(row?.[fieldMapping?.swt_amount || 'SWT Amount'] || 0),
                  nasfundEmployee: parseFloat(row?.[fieldMapping?.nasfund_employee || 'NASFUND Employee'] || 0),
                  nasfundEmployer: parseFloat(row?.[fieldMapping?.nasfund_employer || 'NASFUND Employer'] || 0),
                  netPay: parseFloat(row?.[fieldMapping?.net_pay || 'Net Pay'] || 0),
                  hoursWorked: parseFloat(row?.[fieldMapping?.hours_worked || 'Hours Worked'] || 0),
                  overtimeHours: parseFloat(row?.[fieldMapping?.overtime_hours || 'Overtime Hours'] || 0),
                  rawData: row
                };

                // Validate record
                const validation = await this.validatePayrollRecord(record);
                record.validationStatus = validation?.validationStatus;
                record.validationMessages = validation?.validationMessages;

                // Create integration record
                await this.createIntegrationRecord(record);
                
                processedCount++;
                if (validation?.validationStatus === 'failed') {
                  failedCount++;
                }

                validationResults?.push({
                  employeeName: record?.employeeName,
                  status: validation?.validationStatus,
                  messages: validation?.validationMessages
                });
              } catch (err) {
                failedCount++;
                console.error('Failed to process record:', err);
              }
            }

            // Update session as completed
            await this.updateIntegrationSessionStatus(session?.id, 'completed', {
              processedRecords: processedCount,
              failedRecords: failedCount
            });

            resolve({
              sessionId: session?.id,
              totalRecords: results?.data?.length,
              processedRecords: processedCount,
              failedRecords: failedCount,
              validationResults
            });
          } catch (err) {
            reject(err);
          }
        },
        error: (err) => {
          reject(new Error('Failed to parse CSV file: ' + err?.message));
        }
      });
    });
  },

  // ============================================================================
  // VALIDATION
  // ============================================================================

  async validatePayrollRecord(record) {
    try {
      const { data, error } = await supabase
        ?.rpc('validate_payroll_record', {
          p_integration_record_id: record?.id
        });
      
      if (error) throw error;
      
      return {
        validationStatus: data?.[0]?.validation_status || 'passed',
        validationMessages: data?.[0]?.validation_messages || []
      };
    } catch (err) {
      // Fallback client-side validation
      const messages = [];
      let status = 'passed';

      // Basic validation checks
      if (!record?.employeeNumber) {
        messages?.push('Employee number is required');
        status = 'failed';
      }

      if (record?.grossPay < 0) {
        messages?.push('Gross pay cannot be negative');
        status = 'failed';
      }

      if (record?.netPay > record?.grossPay) {
        messages?.push('Net pay cannot exceed gross pay');
        status = 'failed';
      }

      return { validationStatus: status, validationMessages: messages };
    }
  },

  // ============================================================================
  // AUDIT LOGGING
  // ============================================================================

  async logIntegrationAction(sessionId, action, performedBy, changes = {}) {
    const { error } = await supabase
      ?.from('payroll_integration_audit_log')
      ?.insert({
        integration_session_id: sessionId,
        action_type: action?.type,
        action_description: action?.description,
        performed_by: performedBy,
        entity_type: action?.entityType,
        entity_id: action?.entityId,
        changes_made: changes,
        metadata: action?.metadata
      });
    
    if (error) throw error;
  }
};

export default payrollIntegrationService;